import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Trash2,
  Package,
  Check,
  Building2,
  Contact,
  ShoppingCart,
  AlertTriangle,
  Mail,
} from "lucide-react";
import { t, tf } from "@/lib/i18n";

export default function NewOrder() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user, companies, activeCompanyId, setActiveCompany } = useAuth();
  const isCompany = user?.role === "company";
  const cid = isCompany ? user.company_id : activeCompanyId;

  const [customerId, setCustomerId] = useState("");
  const [customer, setCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ name: "", vat: "", address: "", payment_terms: "" });
  const [useNew, setUseNew] = useState(false);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState("");
  const [saveCustomer, setSaveCustomer] = useState(false);
  const [sendToCustomer, setSendToCustomer] = useState(false);

  const pickCompany = (id) => {
    setActiveCompany(id);
    setCustomer(null);
    setCustomerId("");
    setUseNew(false);
    setCart({});
  };

  const { data: products = [] } = useQuery({
    queryKey: ["products", cid],
    queryFn: async () => (await api.get("/products", { params: { company_id: cid } })).data,
    enabled: !!cid,
  });
  const { data: customers = [] } = useQuery({
    queryKey: ["customers", cid],
    queryFn: async () => (await api.get("/customers", { params: { company_id: cid } })).data,
    enabled: !!cid,
  });

  const custDiscount = customer?.discount || 0;
  const overrides = customer?.price_overrides || {};
  const effectivePrice = (p) => Number(overrides[p.product_id] ?? p.price);
  // combine product discount with customer global discount
  const combinedDiscount = (p) =>
    Math.round((1 - (1 - (p.discount || 0) / 100) * (1 - custDiscount / 100)) * 1000) / 10;

  const pickCustomer = (id) => {
    setCustomerId(id);
    setUseNew(false);
    setCustomer(customers.find((c) => c.customer_id === id) || null);
    setCart({});
  };

  const filtered = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.code.toLowerCase().includes(search.toLowerCase()) ||
      (p.category || "").toLowerCase().includes(search.toLowerCase())
  );

  const packOf = (p) => Math.max(1, p.pack_size || 1);
  const add = (p) => setCart((c) => {
    const cur = c[p.product_id] || { product: p, packs: 0, loose: 0 };
    return { ...c, [p.product_id]: packOf(p) > 1 ? { ...cur, packs: cur.packs + 1 } : { ...cur, loose: cur.loose + 1 } };
  });
  const setPart = (id, field, value) => setCart((c) => ({ ...c, [id]: { ...c[id], [field]: Math.max(0, parseInt(value, 10) || 0) } }));
  const removeItem = (id) =>
    setCart((c) => {
      const n = { ...c };
      delete n[id];
      return n;
    });

  const items = Object.values(cart).map((e) => ({ ...e, qty: e.packs * packOf(e.product) + e.loose }));
  const totals = (() => {
    let net = 0, disc = 0, vat = 0;
    items.forEach(({ product: p, qty }) => {
      const price = effectivePrice(p);
      const d = combinedDiscount(p);
      const gross = price * qty;
      const ld = gross * (d / 100);
      const ln = gross - ld;
      net += ln;
      disc += ld;
      vat += ln * ((p.vat_rate || 0) / 100);
    });
    return { net, disc, vat, grand: net + vat };
  })();

  const customerReady = useNew ? newCustomer.name.trim() : !!customer;

  const submit = useMutation({
    mutationFn: async (status) => {
      const cust = useNew ? newCustomer : customer;
      const payload = {
        company_id: cid,
        customer: {
          name: cust.name, vat: cust.vat || "", address: cust.address || "",
          payment_terms: cust.payment_terms || "", email: cust.email || "", phone: cust.phone || "",
        },
        notes, status, save_customer: useNew && saveCustomer,
        send_to_customer: sendToCustomer && !!(cust.email || "").trim(),
        customer_discount: custDiscount,
        items: items.filter((i) => i.qty > 0).map(({ product: p, qty, packs, loose }) => ({
          product_id: p.product_id, name: p.name, code: p.code,
          price: effectivePrice(p), quantity: qty, packs, loose, pack_size: packOf(p),
          vat_rate: p.vat_rate, discount: combinedDiscount(p),
        })),
      };
      return (await api.post("/orders", payload)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success(tf(data.status === "bozza" ? "Bozza {no} salvata" : "Ordine {no} salvato", { no: data.order_no }));
      if (data.customer_email_sent) toast.success(tf("Ordine inviato via email a {email}", { email: data.customer.email }));
      else if (data.send_to_customer && data.status === "bozza") toast.info(t("L'email al cliente partirà alla conferma dell'ordine"));
      navigate("/orders");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const StepBadge = ({ n, label, done }) => (
    <div className="flex items-center gap-2">
      <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-bold ${done ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
        {done ? <Check className="h-3.5 w-3.5" /> : n}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Nuovo ordine")}</h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
          {!isCompany && <StepBadge n="1" label={t("Azienda")} done={!!cid} />}
          <StepBadge n={!isCompany ? "2" : "1"} label={t("Cliente")} done={customerReady} />
          <StepBadge n={!isCompany ? "3" : "2"} label={t("Prodotti")} done={items.length > 0} />
          <StepBadge n={!isCompany ? "4" : "3"} label={t("Conferma")} done={false} />
        </div>
      </div>

      {/* Step 1: company (agent) */}
      {!isCompany && (
        <div className="bg-white border border-border rounded-xl p-5">
          <Label className="flex items-center gap-2 mb-2"><Building2 className="h-4 w-4 text-primary" /> {t("Azienda per cui stai ordinando")}</Label>
          <Select value={activeCompanyId || ""} onValueChange={pickCompany}>
            <SelectTrigger className="max-w-sm" data-testid="new-order-step1-company-select"><SelectValue placeholder={t("Scegli azienda")} /></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Step 2: customer */}
      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <Label className="flex items-center gap-2"><Contact className="h-4 w-4 text-primary" /> {t("Cliente")}</Label>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <Select value={useNew ? "" : customerId} onValueChange={pickCustomer}>
              <SelectTrigger data-testid="new-order-step2-customer-select"><SelectValue placeholder={t("Scegli dalla rubrica…")} /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.customer_id} value={c.customer_id}>
                    {c.name}{c.discount ? ` · -${c.discount}%` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" variant={useNew ? "default" : "outline"} onClick={() => { setUseNew(true); setCustomer(null); setCustomerId(""); }} data-testid="new-order-new-customer-btn">
            <Plus className="h-4 w-4 mr-1" /> {t("Nuovo cliente")}
          </Button>
        </div>

        {customer && (custDiscount > 0 || Object.keys(overrides).length > 0) && (
          <div className="inline-flex items-center gap-2 text-xs font-medium bg-accent text-accent-foreground px-3 py-1.5 rounded-full">
            {t("Listino personalizzato attivo")}{custDiscount ? ` · sconto ${custDiscount}%` : ""}
          </div>
        )}

        {useNew && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder={t("Nome cliente *")} data-testid="customer-name-input" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} />
            <Input placeholder={t("P.IVA / CF")} value={newCustomer.vat} onChange={(e) => setNewCustomer({ ...newCustomer, vat: e.target.value })} />
            <Input placeholder={t("Indirizzo")} value={newCustomer.address} onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })} />
            <Input placeholder={t("Pagamento")} value={newCustomer.payment_terms} onChange={(e) => setNewCustomer({ ...newCustomer, payment_terms: e.target.value })} />
            <Input type="email" placeholder={t("Email cliente")} data-testid="customer-email-input" value={newCustomer.email || ""} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} className="sm:col-span-2" />
            <label className="flex items-center gap-2 text-xs text-muted-foreground sm:col-span-2">
              <Checkbox checked={saveCustomer} onCheckedChange={(v) => setSaveCustomer(!!v)} data-testid="save-customer-checkbox" />
              {t("Salva il cliente in rubrica")}
            </label>
          </div>
        )}
      </div>

      {/* Step 3 + 4: products & cart */}
      {customerReady ? (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input data-testid="product-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Cerca prodotto…")} className="pl-9" />
            </div>
            {products.length === 0 ? (
              <div className="border border-dashed border-border rounded-xl p-10 text-center text-muted-foreground">
                <Package className="h-9 w-9 mx-auto mb-2" /> {t("Nessun prodotto nel catalogo.")}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {filtered.map((p) => {
                  const low = (p.stock ?? 0) <= (p.low_stock_threshold ?? 10);
                  const hasOverride = overrides[p.product_id] != null;
                  return (
                    <button
                      key={p.product_id}
                      onClick={() => add(p)}
                      data-testid={`add-to-cart-button-${p.product_id}`}
                      className="text-left bg-white border border-border rounded-xl p-3 hover:border-primary/60 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm truncate">{p.name}</p>
                        {cart[p.product_id] && (
                          <span className="bg-primary text-primary-foreground text-xs font-bold h-5 min-w-5 px-1 grid place-items-center rounded-full">{cart[p.product_id].packs * packOf(p) + cart[p.product_id].loose}</span>
                        )}
                      </div>
                      <p className="text-xs font-mono-j text-muted-foreground">{p.code}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-display font-extrabold text-primary">{eur(effectivePrice(p))}{hasOverride && <span className="text-[10px] text-muted-foreground ml-1 line-through">{eur(p.price)}</span>}</span>
                        <span className={`text-xs flex items-center gap-1 ${low ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>
                          {low && <AlertTriangle className="h-3 w-3" />} {p.stock ?? 0} {t("pz")}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border border-border rounded-xl p-5 space-y-3 sticky top-20">
              <h3 className="font-semibold flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-primary" /> {t("Carrello (")}{items.length})</h3>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                <AnimatePresence>
                  {items.length === 0 && <p className="text-sm text-muted-foreground">{t("Aggiungi prodotti dal catalogo.")}</p>}
                  {items.map(({ product: p, qty, packs, loose }) => (
                    <motion.div key={p.product_id} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-1.5 border-b border-border/60 pb-2" data-testid={`cart-item-${p.product_id}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.name}</p>
                          <p className="text-xs text-muted-foreground">{eur(effectivePrice(p))} {t("/ pz")}{combinedDiscount(p) ? ` · -${combinedDiscount(p)}%` : ""}</p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(p.product_id)} data-testid={`cart-remove-${p.product_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                      <QtyInputs p={p} packs={packs} loose={loose} qty={qty} pack={packOf(p)} setPart={setPart} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div className="border-t border-border pt-3 space-y-1 text-sm">
                <Row label={t("Imponibile")} value={eur(totals.net)} />
                <Row label={t("Sconti")} value={`− ${eur(totals.disc)}`} />
                <Row label={t("IVA")} value={eur(totals.vat)} />
                <div className="flex justify-between font-display text-xl font-extrabold pt-2">
                  <span>{t("Totale")}</span>
                  <span className="text-primary" data-testid="cart-grand-total">{eur(totals.grand)}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-1">{t("Il numero progressivo verrà assegnato alla conferma.")}</p>
              </div>

              <Textarea data-testid="order-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder={t("Note ordine (facoltativo)")} />

              <SendToCustomer checked={sendToCustomer} onChange={setSendToCustomer} email={(useNew ? newCustomer.email : customer?.email) || ""} />

              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="h-11 font-semibold" disabled={!items.some((i) => i.qty > 0) || submit.isPending} onClick={() => submit.mutate("bozza")} data-testid="save-draft-btn">{t("Salva bozza")}</Button>
                <Button className="h-11 font-semibold gap-2" disabled={!items.some((i) => i.qty > 0) || submit.isPending} onClick={() => submit.mutate("confermato")} data-testid="cart-checkout-button">
                  <Check className="h-4 w-4" /> {submit.isPending ? t("Invio…") : t("Conferma")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-muted-foreground">
          {t("Seleziona prima un cliente per procedere con i prodotti.")}
        </div>
      )}
    </div>
  );
}

const QtyInputs = ({ p, packs, loose, qty, pack, setPart }) => (
  <div className="flex items-center gap-2 text-xs">
    {pack > 1 && (
      <label className="flex items-center gap-1">{t("Colli")}
        <Input type="number" min="0" value={packs} onChange={(e) => setPart(p.product_id, "packs", e.target.value)} className="h-7 w-14 px-1.5 text-center" data-testid={`cart-packs-${p.product_id}`} />
      </label>
    )}
    <label className="flex items-center gap-1">{pack > 1 ? t("Sfusi") : t("Pezzi")}
      <Input type="number" min="0" value={loose} onChange={(e) => setPart(p.product_id, "loose", e.target.value)} className="h-7 w-14 px-1.5 text-center" data-testid={`cart-loose-${p.product_id}`} />
    </label>
    <span className="ml-auto text-muted-foreground" data-testid={`cart-total-pieces-${p.product_id}`}>{pack > 1 ? tf("{n} pz · collo da {p}", { n: qty, p: pack }) : tf("{n} pz", { n: qty })}</span>
  </div>
);

const SendToCustomer = ({ checked, onChange, email }) => {
  const hasEmail = !!email.trim();
  return (
    <label className={`flex items-start gap-2 text-sm rounded-lg border border-border p-3 ${hasEmail ? "" : "opacity-60"}`} data-testid="send-to-customer-row">
      <Checkbox checked={checked && hasEmail} disabled={!hasEmail} onCheckedChange={(v) => onChange(!!v)} data-testid="send-to-customer-checkbox" className="mt-0.5" />
      <span>
        <span className="font-medium flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {t("Invia ordine anche al cliente")}</span>
        <span className="block text-xs text-muted-foreground" data-testid="send-to-customer-hint">
          {hasEmail ? tf("Email con PDF a {email} alla conferma dell'ordine", { email }) : t("Il cliente non ha un'email in rubrica")}
        </span>
      </span>
    </label>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between text-muted-foreground">
    <span>{label}</span>
    <span>{value}</span>
  </div>
);
